import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // 👇 Clique na imagem (event delegation)
    this.addEventListener('click', this.#handleImageClick);

    // 👇 Atualiza as thumbs quando o drawer abrir
    this.addEventListener(DialogOpenEvent.eventName, () => {
      setTimeout(() => {
        this.updateCustomilyThumbs();
      }, 100);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // Remove listener do clique
    this.removeEventListener('click', this.#handleImageClick);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }

  /**
   * Atualiza as imagens do carrinho com o thumb do Customily
   */
  updateCustomilyThumbs() {
    const items = this.querySelectorAll('.line-item__media-wrapper');

    items.forEach((item) => {
      const thumbUrl = item.getAttribute('data-thumb-url');
      const img = item.querySelector('img');

      if (thumbUrl && img && img.src !== thumbUrl) {
        img.src = thumbUrl;
      }
    });
  }

  /**
   * Abre o modal ao clicar na imagem
   */
  #handleImageClick = (event) => {
    const wrapper = event.target.closest('.line-item__media-wrapper');
    if (!wrapper) return;

    const thumbUrl = wrapper.getAttribute('data-thumb-url');
    if (!thumbUrl) return;

    if (typeof window.openModal === 'function') {
      window.openModal(thumbUrl);
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}